import { Download, Pause, PhoneCallbackOutlined, PlayArrow } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Agent,
  VOICEMAIL_STATUS_COLORS,
  VOICEMAIL_STATUS_DISPLAY,
  VoicemailDirection,
  VoicemailLog,
  VoicemailStatus,
} from '../../../../interfaces/voicemail';
import voiceService from '../../../../services/appointments/calls';
import { formatWithTimezone } from '../../../../util/dateFormat';
import { PhoneOutgoing } from '../../../common/PhoneOutgoing';
import './VoicemailDetailModal.css';

type Props = {
  open: boolean;
  voicemail: VoicemailLog | null;
  onClose: () => void;
  onUpdate?: (updatedVoicemail: VoicemailLog) => void;
};

export const VoicemailDetailModal = ({ open, voicemail, onClose, onUpdate }: Props) => {
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<VoicemailStatus>(VoicemailStatus.PENDING);
  const [assigneeId, setAssigneeId] = useState<number | null>(null);
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const PLAYBACK_SPEEDS = [1, 1.5, 2, 3] as const;

  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<string>('00:00');
  type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number];

  const [recordingProgress, setRecordingProgress] = useState<number>(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(1);

  const notify = useNotify();
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const formatDuration = (seconds: number) => {
    if (typeof seconds !== 'number' || isNaN(seconds)) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDurationLong = (duration?: number) => {
    if (typeof duration !== 'number' || isNaN(duration)) return '0 minutes, 0 seconds';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes} minutes, ${seconds} seconds`;
  };

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const updateProgress = useCallback(() => {
    if (audioRef.current) {
      const audio = audioRef.current;
      const progress = (audio.currentTime / audio.duration) * 100;
      setRecordingProgress(progress || 0);
      setCurrentTime(formatTime(audio.currentTime || 0));
    }
  }, []);

  const startProgressUpdates = useCallback(() => {
    stopProgressUpdates();
    progressIntervalRef.current = setInterval(() => {
      updateProgress();
    }, 100);
  }, [updateProgress, stopProgressUpdates]);

  const handleSpeedChange = useCallback(() => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
    const newSpeed = PLAYBACK_SPEEDS[nextIndex];

    setPlaybackSpeed(newSpeed);

    if (currentAudio) {
      currentAudio.playbackRate = newSpeed;
    }
  }, [playbackSpeed, currentAudio]);

  const handlePlayPause = useCallback(async () => {
    if (!voicemail?.recordingUrl) {
      notify('No recording available', 'warning');
      return;
    }

    if (!currentAudio) {
      const audio = new Audio(voicemail.recordingUrl);

      audio.onloadedmetadata = () => {
        updateProgress();
      };

      audio.ontimeupdate = () => {
        updateProgress();
      };

      audio.onplay = () => {
        setIsPlaying(true);
        startProgressUpdates();
      };

      audio.onpause = () => {
        setIsPlaying(false);
        stopProgressUpdates();
      };

      audio.onended = () => {
        setIsPlaying(false);
        setRecordingProgress(0);
        setCurrentTime('00:00');
        stopProgressUpdates();
      };

      audio.onerror = () => {
        notify('Error playing voicemail', 'error');
        setIsPlaying(false);
        stopProgressUpdates();
      };

      audio.playbackRate = playbackSpeed;
      audioRef.current = audio;
      setCurrentAudio(audio);

      try {
        await audio.play();
      } catch (error) {
        notify('Error playing voicemail', 'error');
      }
    } else {
      if (isPlaying) {
        currentAudio.pause();
      } else {
        try {
          await currentAudio.play();
        } catch (error) {
          notify('Error playing voicemail', 'error');
        }
      }
    }
  }, [
    voicemail,
    currentAudio,
    isPlaying,
    notify,
    playbackSpeed,
    updateProgress,
    startProgressUpdates,
    stopProgressUpdates,
  ]);

  const handleProgressBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) {
      return;
    }
    const audio = audioRef.current;
    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickPercent = clickX / rect.width;
    const newTime = clickPercent * (audio.duration || 0);
    audio.currentTime = newTime;
    updateProgress();
  };

  const handleDownloadRecording = () => {
    if (voicemail?.recordingUrl) {
      window.open(voicemail.recordingUrl, '_blank');
    }
  };

  const getStatusColor = (status: VoicemailStatus) => {
    return VOICEMAIL_STATUS_COLORS[status] || '#757575';
  };

  const isInbound = !voicemail?.direction || voicemail.direction === VoicemailDirection.INBOUND;
  const isOutbound = voicemail?.direction === VoicemailDirection.OUTBOUND;

  const handleSave = async () => {
    if (!voicemail) {
      return;
    }

    try {
      setIsLoading(true);

      const updateData: any = {};
      if (notes !== voicemail.notes) {
        updateData.notes = notes;
      }
      if (status !== voicemail.status) {
        updateData.status = status;
      }
      if (assigneeId !== voicemail.assigneeId) {
        updateData.assigneeId = assigneeId;
      }

      if (Object.keys(updateData).length === 0) {
        onClose();
        return;
      }

      const response = await voiceService.updateVoicemail(voicemail.id, updateData);

      if (response.success && response.data) {
        notify('Voicemail updated successfully', 'success');
        onUpdate?.(response.data);
        onClose();
      } else {
        notify('Failed to update voicemail', 'error');
      }
    } catch (error) {
      notify('Failed to update voicemail', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Stop audio when closing
    stopProgressUpdates();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
      setCurrentAudio(null);
      setIsPlaying(false);
      setCurrentTime('00:00');
      setRecordingProgress(0);
    }
    onClose();
  };

  useEffect(() => {
    if (voicemail) {
      setNotes(voicemail.notes || '');
      setStatus(voicemail.status);
      setAssigneeId(voicemail.assigneeId ?? null);
    }
  }, [voicemail]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (open) {
        setIsLoadingAgents(true);
        try {
          const response = await voiceService.getAvailableAgents();
          if (response.success && response.data) {
            setAvailableAgents(response.data);
          }
        } catch (error) {
          console.error('Error fetching agents:', error);
        } finally {
          setIsLoadingAgents(false);
        }
      }
    };

    fetchAgents();
  }, [open]);

  useEffect(() => {
    // Cleanup audio on unmount or modal close
    return () => {
      stopProgressUpdates();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, [stopProgressUpdates]);

  if (!voicemail) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      sx={{
        '& .MuiDialog-container > .MuiPaper-elevation': {
          borderRadius: '12px',
          maxHeight: '90vh',
          maxWidth: '40vw',
        },
      }}>
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        }}>
        {/* Gray top bar with close button */}
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '4px 10px',
          }}>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Header with direction icon, title, and status chip */}
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          padding='0 24px'
          marginTop={2}>
          <Box display='flex' alignItems='center' gap={1}>
            {isInbound ? (
              <PhoneCallbackOutlined sx={{ color: '#AF77BE', fontSize: 20 }} />
            ) : (
              <PhoneOutgoing sx={{ color: '#389FBA', fontSize: 20 }} />
            )}

            <Typography variant='h6' fontWeight={600}>
              {isInbound ? 'Incoming Voicemail' : 'Outgoing Voicemail'}
            </Typography>
          </Box>

          <Chip
            label={VOICEMAIL_STATUS_DISPLAY[status]}
            size='small'
            sx={{
              backgroundColor: getStatusColor(status),
              color: 'white',
              fontSize: '12px',
            }}
          />
        </Box>

        {/* From/To Information */}
        <Box pt={3} pb={2} borderBottom='2px solid #e9ecef'>
          <Box display='flex' justifyContent='space-between' gap={2} pl={3} pr={3} pb={1}>
            <Box flex={1}>
              <Typography variant='subtitle2' color='textSecondary' gutterBottom>
                From:
              </Typography>
              <Box border='1px solid #E0E0E0' p={1} borderRadius='4px'>
                <Typography variant='subtitle1'>
                  {voicemail.fromName || (isInbound ? 'Member' : 'Agent')}
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  {voicemail.fromNumber}
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  {isInbound ? 'Member' : 'Agent'}
                </Typography>
              </Box>
            </Box>

            <Box flex={1}>
              <Typography variant='subtitle2' color='textSecondary' gutterBottom>
                To:
              </Typography>
              <Box border='1px solid #E0E0E0' p={1} borderRadius='4px'>
                <Typography variant='subtitle1'>
                  {voicemail.toName || (isInbound ? 'Agent' : 'Member')}
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  {voicemail.toNumber}
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  {isInbound ? 'Agent' : 'Member'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        {/* Duration and Date/Time */}
        <Box
          display='flex'
          flexDirection='column'
          gap={1}
          pl={3}
          pr={3}
          pt={3}
          pb={2}
          borderBottom='2px solid #e9ecef'>
          <Box display='flex' gap={1}>
            <Typography variant='body1' color='textSecondary' gutterBottom width='12rem'>
              Duration:
            </Typography>
            <Typography variant='body1'>{formatDurationLong(voicemail.duration)}</Typography>
          </Box>

          <Box display='flex' gap={1}>
            <Typography variant='body1' color='textSecondary' gutterBottom width='12rem'>
              Date / Time:
            </Typography>
            <Typography variant='body1'>
              {formatWithTimezone(voicemail.createdAt, undefined, 'MMM DD, YYYY [at] HH:mm [PT]')}
            </Typography>
          </Box>
        </Box>

        {/* Voicemail Recording */}
        {voicemail.recordingUrl && (
          <Box p={3} borderBottom='1px solid #e9ecef'>
            <Typography
              variant='subtitle1'
              fontWeight='bold'
              mb={2}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Voicemail Recording
            </Typography>

            <Box
              sx={{
                borderRadius: 2,
                p: 2,
                border: '1px solid #e9ecef',
              }}>
              <Box display='flex' alignItems='center' gap={1}>
                <IconButton
                  onClick={handlePlayPause}
                  sx={{
                    backgroundColor: '#389FBA',
                    color: 'white',
                    '&:hover': { backgroundColor: '#2d7a96' },
                    width: 32,
                    height: 32,
                  }}>
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>

                <Button
                  onClick={handleSpeedChange}
                  size='small'
                  sx={{
                    minWidth: '40px',
                    height: '28px',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    border: '1px solid #ddd',
                    '&:hover': {
                      backgroundColor: '#e0e0e0',
                      border: '1px solid #bbb',
                    },
                    fontSize: '11px',
                    fontWeight: 600,
                    borderRadius: '6px',
                  }}>
                  {playbackSpeed}x
                </Button>

                <Box flex={1} ml={1}>
                  <Box
                    onClick={handleProgressBarClick}
                    sx={{
                      cursor: 'pointer',
                      '&:hover .MuiLinearProgress-bar': {
                        backgroundColor: '#2d7a96',
                      },
                    }}>
                    <LinearProgress
                      variant='determinate'
                      value={recordingProgress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#e9ecef',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#389FBA',
                          transition: 'background-color 0.2s',
                        },
                      }}
                    />
                  </Box>
                  <Box display='flex' justifyContent='space-between' mt={0.5}>
                    <Typography variant='caption' color='textSecondary'>
                      {currentTime}
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {formatDuration(voicemail.duration)}
                    </Typography>
                  </Box>
                </Box>

                <IconButton onClick={handleDownloadRecording} size='small' sx={{ ml: 1 }}>
                  <Download />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {/* Status and Notes Section */}
        <Box p={3}>
          <Typography variant='subtitle1' fontWeight='bold' mb={2}>
            Status & Notes
          </Typography>

          {/* Status Dropdown - Disabled for outgoing voicemails */}
          <Box mb={3}>
            <FormControl fullWidth size='small' disabled={isOutbound}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as VoicemailStatus)}
                label='Status'>
                {[VoicemailStatus.PENDING, VoicemailStatus.MANUALLY_RESOLVED].map((statusValue) => (
                  <MenuItem key={statusValue} value={statusValue}>
                    <Box display='flex' alignItems='center' gap={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: VOICEMAIL_STATUS_COLORS[statusValue],
                        }}
                      />
                      <Typography>{VOICEMAIL_STATUS_DISPLAY[statusValue]}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isOutbound && (
              <Typography variant='caption' color='textSecondary' sx={{ mt: 1, display: 'block' }}>
                Status is automatically managed for outgoing voicemails
              </Typography>
            )}
          </Box>

          {/* Assignee Dropdown */}
          <Box mb={3}>
            <FormControl fullWidth size='small' disabled={isLoadingAgents}>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={assigneeId ?? ''}
                onChange={(e) => setAssigneeId(e.target.value ? Number(e.target.value) : null)}
                label='Assigned To'>
                <MenuItem value=''>
                  <em>Unassigned</em>
                </MenuItem>
                {availableAgents
                  .filter((agent) => agent.id !== voicemail?.assigneeId)
                  .map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.name || `Agent ${agent.id}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {voicemail?.assignee && (
              <Typography variant='caption' color='textSecondary' sx={{ mt: 1, display: 'block' }}>
                Currently assigned to: {voicemail.assignee.name || `Agent ${voicemail.assignee.id}`}
              </Typography>
            )}
          </Box>

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder='Add notes about this voicemail...'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            variant='outlined'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid #e9ecef' }}>
        <Button
          variant='contained'
          fullWidth
          onClick={handleSave}
          disabled={isLoading}
          sx={{
            backgroundColor: '#007bff',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#0056b3' },
          }}>
          {isLoading ? 'Saving Changes...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
